import React, { useState, useEffect, useLayoutEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button, SparkIcon, Checkbox, TooltipTrigger, PopoverShell, PopoverMenuItem, Modal, SparkModalPortal, Spinner, DataTable, sparkToast, ProgressCircle, Spinner as SparkSpinner } from '@spark';
import './DatasetsGroupedTableView.css';
import { data_from_server_delete } from '@/shared/http/client';
import { formatDate, formatDateTime } from '@/shared/utils/helpers';
import { taskUrlForUsecase } from '@/features/tasks/utils/navigation';
import { GlobalContext } from '@/app/providers/globalState';
import { useExport } from '@/shared/hooks/useExport';
import { useAuth } from '@/features/auth/AuthContext';
import { DatasetsBulkActionsBar } from '@/features/datasets/components/datasets-page/DatasetsBulkActionsBar.jsx';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';

/**
 * Resolve an LLM usecase for /download-dataset when the user is on the global Datasets page.
 */
function findDownloadUsecase(dataset, isBenchmark, usecases, getCurrentUsecase) {
    const datasetName = dataset?.name || dataset?.dataset_name || dataset?.value;
    const listKey = isBenchmark ? 'benchmarks' : 'datasets';

    const hasTask = (u) => u && u._id && u.task_type;

    if (datasetName && usecases?.length) {
        const g = dataset.group_and_usecase_names;
        if (g && typeof g === 'object') {
            for (const [groupName, names] of Object.entries(g)) {
                for (const usecaseName of Array.isArray(names) ? names : []) {
                    const u = usecases.find(
                        (x) => x.group_name === groupName && x.usecase_name === usecaseName
                    );
                    if (hasTask(u)) return u;
                }
            }
        }

        const linked = dataset.linked_tasks;
        if (Array.isArray(linked) && linked.length > 0) {
            const id = linked[0].usecase_id;
            const u = usecases.find((x) => String(x._id) === String(id));
            if (hasTask(u)) return u;
        }

        const u = usecases.find(
            (x) => Array.isArray(x[listKey]) && x[listKey].includes(datasetName)
        );
        if (hasTask(u)) return u;
    }

    const cur = getCurrentUsecase?.();
    if (datasetName && hasTask(cur) && Array.isArray(cur[listKey]) && cur[listKey].includes(datasetName)) {
        return cur;
    }

    return null;
}

const EllipsisTextWithTooltip = ({ text, maxWidth = 180, style = {}, ...props }) => (
    <TooltipTrigger text={text}>
        <div
            style={{
                maxWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                ...style
            }}
            {...props}
        >
            {text}
        </div>
    </TooltipTrigger>
);

const taskCellTextStyle = {
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    fontSize: 14,
};

/** Multi-task picker: portals a clickable menu (Spark tooltips are not interactive). */
const LinkedTasksHoverMenu = ({ items, displayText, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [menuPanelStyle, setMenuPanelStyle] = useState({});
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const closeTimerRef = useRef(null);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current != null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const scheduleClose = useCallback(() => {
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
    }, [clearCloseTimer]);

    const openMenu = useCallback(() => {
        clearCloseTimer();
        setOpen(true);
    }, [clearCloseTimer]);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return undefined;
        const update = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            setMenuPanelStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: rect.left,
                zIndex: 10000,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open]);

    useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            const inTrigger = triggerRef.current?.contains(e.target);
            const inPanel = panelRef.current?.contains(e.target);
            if (!inTrigger && !inPanel) setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    if (!items.length) {
        return <span style={{ ...taskCellTextStyle, cursor: 'default' }}>{displayText}</span>;
    }

    if (items.length === 1) {
        return (
            <div
                style={taskCellTextStyle}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect(items[0]);
                }}
            >
                {displayText}
            </div>
        );
    }

    return (
        <>
            <span
                ref={triggerRef}
                style={{ position: 'relative', display: 'inline-flex', maxWidth: 200 }}
                onMouseEnter={openMenu}
                onMouseLeave={scheduleClose}
            >
                <div
                    style={taskCellTextStyle}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen((prev) => !prev);
                    }}
                >
                    {displayText}
                </div>
            </span>
            {open
                ? createPortal(
                    <div
                        ref={panelRef}
                        style={menuPanelStyle}
                        onMouseEnter={openMenu}
                        onMouseLeave={scheduleClose}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PopoverShell tip={false} panelClassName="spark-popover-menu-panel spark-popover-menu-panel-regular">
                            <div className="spark-popover-menu-list" role="menu">
                                {items.map((item) => (
                                    <PopoverMenuItem
                                        key={item.key}
                                        label={item.label}
                                        onClick={() => {
                                            setOpen(false);
                                            onSelect(item);
                                        }}
                                    />
                                ))}
                            </div>
                        </PopoverShell>
                    </div>,
                    document.body,
                )
                : null}
        </>
    );
};

const TaskDisplay = ({ groupAndUsecaseNames, linkedTasks }) => {
    const navigate = useNavigate();
    const { allUsecases, setCurrentUsecaseId } = useContext(GlobalContext);

    const handleTaskClick = (groupName, usecaseName) => {
        if (!allUsecases || allUsecases.length === 0) return;
        const matchedUsecase = allUsecases.find(
            (u) => u.group_name === groupName && u.usecase_name === usecaseName
        );
        if (!matchedUsecase) return;
        const url = taskUrlForUsecase(matchedUsecase, { objective: matchedUsecase._id });
        if (!url) return;
        setCurrentUsecaseId(matchedUsecase._id);
        navigate(url);
    };

    const handleLinkedTaskClick = (usecaseId) => {
        if (!allUsecases || allUsecases.length === 0) return;
        const uc = allUsecases.find(u => String(u._id) === String(usecaseId));
        if (!uc) return;
        const url = taskUrlForUsecase(uc, { objective: uc._id });
        if (!url) return;
        setCurrentUsecaseId(usecaseId);
        navigate(url);
    };

    if (linkedTasks && linkedTasks.length > 0) {
        const displayText = linkedTasks.length === 1
            ? linkedTasks[0].usecase_name
            : `${linkedTasks.length} tasks`;
        const items = linkedTasks.map((lt) => ({
            key: String(lt.usecase_id),
            label: `${lt.group_name}: ${lt.usecase_name}`,
            usecaseId: lt.usecase_id,
        }));
        return (
            <LinkedTasksHoverMenu
                items={items}
                displayText={displayText}
                onSelect={(item) => handleLinkedTaskClick(item.usecaseId)}
            />
        );
    }

    if (!groupAndUsecaseNames || Object.keys(groupAndUsecaseNames).length === 0) {
        return <span style={{ color: 'var(--semantic-content-tertiary)' }}></span>;
    }

    const entries = Object.entries(groupAndUsecaseNames);
    const firstGroupName = entries[0]?.[0];
    const displayText = firstGroupName && groupAndUsecaseNames[firstGroupName]
        ? `${groupAndUsecaseNames[firstGroupName].join(', ')}`
        : 'Tasks available';

    const items = entries.flatMap(([groupName, tasks]) =>
        (Array.isArray(tasks) ? tasks : []).map((taskName) => ({
            key: `${groupName}:${taskName}`,
            label: `${groupName}: ${taskName}`,
            groupName,
            taskName,
        }))
    );

    const cellLabel = (
        <>
            {displayText}
            {entries.length > 1 && '...'}
        </>
    );

    return (
        <LinkedTasksHoverMenu
            items={items}
            displayText={cellLabel}
            onSelect={(item) => handleTaskClick(item.groupName, item.taskName)}
        />
    );
};

const panelStyle = {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 4px)',
    zIndex: 1400,
};

const DatasetOverflowMenu = ({ dataset, isBenchmark, isManual, isDownloading, downloadPct, onDownload, onDelete, isLinked }) => {
    const [open, setOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    return (
        <>
            <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
                <Button
                    variant="ghost"
                    size="small"
                    aria-label="Dataset actions"
                    icon={<SparkIcon slug="dots" size="small" />}
                    onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                />
                {open && (
                    <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
                        <PopoverShell tip={false} panelClassName="spark-popover-menu-panel spark-popover-menu-panel-regular">
                            <div className="spark-popover-menu-list" role="menu">
                                <PopoverMenuItem
                                    label={isDownloading ? 'Downloading...' : 'Download dataset'}
                                    onClick={() => {
                                        setOpen(false);
                                        if (!isDownloading) onDownload();
                                    }}
                                />
                                {isLinked ? (
                                    <TooltipTrigger text="Cannot delete dataset that is linked to a task" placement="top">
                                        <PopoverMenuItem label="Delete Dataset" state="disabled" />
                                    </TooltipTrigger>
                                ) : (
                                    <PopoverMenuItem
                                        label="Delete Dataset"
                                        disableWhenReadOnly
                                        onClick={() => {
                                            setOpen(false);
                                            setShowDeleteConfirm(true);
                                        }}
                                    />
                                )}
                            </div>
                        </PopoverShell>
                    </div>
                )}
            </div>

            <SparkModalPortal
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                size="text"
            >
                <Modal
                    className="datasets-delete-confirm-modal"
                    feedbackType="warning"
                    title="Delete dataset?"
                    showSubtitle={false}
                    showScrollBar={false}
                    fixedHeight={false}
                    danger
                    primaryLabel="Delete"
                    primaryDisableWhenReadOnly
                    tertiaryLabel="Cancel"
                    onPrimaryClick={() => {
                        setShowDeleteConfirm(false);
                        onDelete();
                    }}
                    onTertiaryClick={() => setShowDeleteConfirm(false)}
                    onCloseClick={() => setShowDeleteConfirm(false)}
                >
                    <p>This will permanently delete this dataset.</p>
                    <p style={{ color: 'var(--semantic-feedback-critical-on-surface)' }}>
                        This action cannot be undone.
                    </p>
                </Modal>
            </SparkModalPortal>
        </>
    );
};

const DatasetsGroupedTableView = ({ groupedDatasets, onDatasetAction, onBulkDelete, onBulkDownload, isBulkOperationInProgress = false }) => {
    const [expandedGroups, setExpandedGroups] = useState({});
    const [allExpanded, setAllExpanded] = useState(true);
    const { role } = useAuth();
    const { getCurrentUsecase, allUsecases, refreshUsecases } = useContext(GlobalContext);
    const { exportData } = useExport();

    const [selectedDatasetKeys, setSelectedDatasetKeys] = useState([]);
    const [downloadingDatasetKeys, setDownloadingDatasetKeys] = useState([]);
    const [downloadProgressByKey, setDownloadProgressByKey] = useState({});
    const inFlightDownloadKeysRef = useRef(new Set());

    const allDatasets = useMemo(() => {
        const datasets = [];
        groupedDatasets.forEach(group => {
            group.datasets.forEach(dataset => {
                const datasetKey = dataset.key || dataset.name || dataset.dataset_name;
                datasets.push({
                    ...dataset,
                    _groupId: group.group_id || group.group_name,
                    _groupName: group.group_name,
                    _key: datasetKey,
                });
            });
        });
        return datasets;
    }, [groupedDatasets]);

    const selectedDatasets = useMemo(() => {
        return allDatasets.filter(d => selectedDatasetKeys.includes(d._key));
    }, [allDatasets, selectedDatasetKeys]);

    const isSelectionMode = role !== 'Viewer' && selectedDatasetKeys.length > 0;
    const showCheckboxColumn = role !== 'Viewer';

    const handleCheckboxChange = useCallback((datasetKey, checked) => {
        if (checked) {
            setSelectedDatasetKeys(prev => [...prev, datasetKey]);
        } else {
            setSelectedDatasetKeys(prev => prev.filter(k => k !== datasetKey));
        }
    }, []);

    const handleSelectAll = useCallback(() => {
        const allKeys = allDatasets.map(d => d._key);
        setSelectedDatasetKeys(allKeys);
    }, [allDatasets]);

    const handleDeselectAll = useCallback(() => {
        setSelectedDatasetKeys([]);
    }, []);

    const clearDatasetDownload = useCallback((datasetKey) => {
        inFlightDownloadKeysRef.current.delete(datasetKey);
        setDownloadingDatasetKeys(prev => prev.filter(k => k !== datasetKey));
        setDownloadProgressByKey(prev => {
            const next = { ...prev };
            delete next[datasetKey];
            return next;
        });
    }, []);

    const handleDownloadInternal = useCallback(async (dataset, isBenchmark) => {
        const datasetKey = dataset?.key || dataset?.name || dataset?.dataset_name || dataset?._key;

        if (inFlightDownloadKeysRef.current.has(datasetKey)) {
            return;
        }

        let usecases = allUsecases;
        if (!usecases?.length && refreshUsecases) {
            usecases = await new Promise((resolve) => {
                let settled = false;
                const finish = (ucs) => {
                    if (settled) return;
                    settled = true;
                    resolve(ucs || []);
                };
                refreshUsecases((ucs) => finish(ucs));
                setTimeout(() => finish([]), 20000);
            });
        }

        const usecase = findDownloadUsecase(dataset, isBenchmark, usecases, getCurrentUsecase);
        const datasetName = dataset?.name || dataset?.dataset_name;
        if (!datasetName) {
            sparkToast.error('Cannot download: missing dataset name');
            return;
        }

        const payload =
            usecase?._id && usecase?.task_type
                ? {
                    llm_usecase_id: usecase._id,
                    is_benchmark: !!isBenchmark,
                    task_type: usecase.task_type,
                    dataset_name: datasetName,
                }
                : {
                    llm_usecase_id: null,
                    client_id: DEFAULT_CLIENT_ID,
                    is_benchmark: !!isBenchmark,
                    task_type: null,
                    dataset_name: datasetName,
                };

        inFlightDownloadKeysRef.current.add(datasetKey);

        setDownloadingDatasetKeys(prev => [...prev, datasetKey]);
        setDownloadProgressByKey(prev => ({ ...prev, [datasetKey]: null }));

        const displayName = datasetName || 'dataset';
        const filename = `${displayName}.json`;
        exportData(
            'download-dataset',
            payload,
            filename,
            () => clearDatasetDownload(datasetKey),
            (error) => {
                clearDatasetDownload(datasetKey);
                sparkToast.error(typeof error === 'string' ? error : 'Download failed');
            },
            {
                onProgress: (pct) => {
                    setDownloadProgressByKey(prev => ({ ...prev, [datasetKey]: pct }));
                },
            }
        );
    }, [exportData, clearDatasetDownload, allUsecases, refreshUsecases, getCurrentUsecase]);

    const bulkDownloadPercent = useMemo(() => {
        if (downloadingDatasetKeys.length === 0) return null;
        const keys = downloadingDatasetKeys;
        const nums = keys.map((k) => downloadProgressByKey[k]).filter((v) => typeof v === 'number');
        if (nums.length !== keys.length) return null;
        return Math.round(nums.reduce((a, b) => a + b, 0) / nums.length);
    }, [downloadingDatasetKeys, downloadProgressByKey]);

    const handleBulkDownload = useCallback(() => {
        if (selectedDatasets.length === 0) return;

        if (onBulkDownload) {
            onBulkDownload(selectedDatasets);
        } else {
            selectedDatasets.forEach(dataset => {
                const isBenchmark = dataset._groupName === 'Benchmarks';
                handleDownloadInternal(dataset, isBenchmark);
            });
        }
    }, [selectedDatasets, onBulkDownload, handleDownloadInternal]);

    const handleBulkDeleteAction = useCallback(() => {
        if (selectedDatasets.length === 0) return;

        const deletableDatasets = selectedDatasets.filter(
            d =>
                (!d.group_and_usecase_names || Object.keys(d.group_and_usecase_names).length === 0) &&
                (!d.linked_tasks || d.linked_tasks.length === 0)
        );

        if (deletableDatasets.length === 0) {
            sparkToast.warning('All selected datasets are linked to tasks and cannot be deleted');
            return;
        }

        if (onBulkDelete) {
            onBulkDelete(deletableDatasets, () => {
                setSelectedDatasetKeys([]);
            });
        }
    }, [selectedDatasets, onBulkDelete]);

    const handleDownload = (dataset, isBenchmark) => {
        handleDownloadInternal(dataset, isBenchmark);
    };

    useEffect(() => {
        setExpandedGroups(prev => {
            const next = { ...prev };
            groupedDatasets.forEach(group => {
                const groupKey = group.group_id || group.group_name;
                if (typeof next[groupKey] === 'undefined') {
                    next[groupKey] = true;
                }
            });
            Object.keys(next).forEach(name => {
                if (!groupedDatasets.find(g => (g.group_id || g.group_name) === name)) {
                    delete next[name];
                }
            });
            return next;
        });
    }, [groupedDatasets]);

    const toggleAllGroups = useCallback(() => {
        const newExpanded = !allExpanded;
        setAllExpanded(newExpanded);
        const newState = {};
        groupedDatasets.forEach(group => {
            const groupKey = group.group_id || group.group_name;
            newState[groupKey] = newExpanded;
        });
        setExpandedGroups(newState);
    }, [allExpanded, groupedDatasets]);

    const toggleGroup = useCallback((groupKey) => {
        setExpandedGroups(prev => {
            const newState = { ...prev, [groupKey]: !prev[groupKey] };
            const allGroupsExpanded = groupedDatasets.every(g => newState[g.group_id || g.group_name] === true);
            const allGroupsCollapsed = groupedDatasets.every(g => newState[g.group_id || g.group_name] === false);

            if (allGroupsExpanded) {
                setAllExpanded(true);
            } else if (allGroupsCollapsed) {
                setAllExpanded(false);
            }

            return newState;
        });
    }, [groupedDatasets]);

    const formatFileSize = (size) => {
        if (!size || size === 'Unknown') return '';
        if (typeof size === 'number') {
            const units = ['B', 'KB', 'MB', 'GB'];
            let unitIndex = 0;
            let fileSize = size;
            while (fileSize >= 1024 && unitIndex < units.length - 1) {
                fileSize /= 1024;
                unitIndex++;
            }
            return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
        }
        return size;
    };

    const handleDeleteDataset = useCallback((dataset, isBenchmark, isManual) => {
        const currentUsecase = getCurrentUsecase();
        const clientId = currentUsecase?.client_id || "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";

        const payload = {
            client_id: clientId,
            dataset: dataset.name || dataset.dataset_name,
            is_benchmark: isBenchmark,
            is_manual_model_predictions: !!isManual
        };

        data_from_server_delete(
            'datasets',
            payload,
            (response) => {
                if (response.success) {
                    sparkToast.success('Dataset deleted successfully');
                    if (onDatasetAction) {
                        onDatasetAction('delete', dataset, isBenchmark, isManual);
                    }
                } else {
                    sparkToast.error('Failed to delete dataset');
                }
            },
            (error) => {
                sparkToast.error(`Error deleting dataset: ${error}`);
            }
        );
    }, [getCurrentUsecase, onDatasetAction]);

    const headerCheckboxStatus =
        selectedDatasetKeys.length === 0 ? 'inactive' :
        selectedDatasetKeys.length === allDatasets.length ? 'active' :
        'indeterminate';

    const handleHeaderCheckboxChange = useCallback(() => {
        if (selectedDatasetKeys.length === 0) {
            handleSelectAll();
        } else {
            handleDeselectAll();
        }
    }, [selectedDatasetKeys.length, handleSelectAll, handleDeselectAll]);

    /* ── DataTable column definitions ─────────────────────────────────────── */

    const columns = useMemo(() => {
        const cols = [
            {
                key: 'spacer',
                width: 48,
                title: (
                    <Button
                        variant="ghost"
                        size="small"
                        className="spark-table-expand-toggle-btn"
                        aria-expanded={allExpanded}
                        aria-label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                        onClick={toggleAllGroups}
                        icon={
                            <SparkIcon
                                slug={allExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                size="small"
                            />
                        }
                    />
                ),
                render: () => null,
            },
            {
                key: 'name',
                dataIndex: 'name',
                title: 'Dataset name',
                flex: 2,
                minWidth: 150,
                render: (val, record) => {
                    const datasetKey = record.key || record.name || record.dataset_name;
                    const isDl = downloadingDatasetKeys.includes(datasetKey);
                    const dlPct = downloadProgressByKey[datasetKey];
                    const displayName = getUserFacingDatasetName(record.name || record.dataset_name) || 'Unnamed Dataset';
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isDl && (typeof dlPct === 'number'
                                ? <ProgressCircle status="in-progress" value={Math.min(100, Math.round(dlPct))} />
                                : <SparkSpinner size="small" />
                            )}
                            <p
                                className="spark-table-cell-title-text"
                                style={{
                                    textDecoration: 'underline',
                                    maxWidth: isDl ? 240 : 290,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={displayName}
                            >
                                {displayName}
                            </p>
                        </div>
                    );
                },
            },
        ];

        if (showCheckboxColumn) {
            cols.unshift({
                key: 'checkbox',
                width: 48,
                title: (
                    <Checkbox
                        label=""
                        status={headerCheckboxStatus}
                        onChange={handleHeaderCheckboxChange}
                    />
                ),
                render: (_, record) => {
                    const datasetKey = record.key || record.name || record.dataset_name;
                    const isSelected = selectedDatasetKeys.includes(datasetKey);
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                label=""
                                status={isSelected ? 'active' : 'inactive'}
                                onChange={() => handleCheckboxChange(datasetKey, !isSelected)}
                            />
                        </div>
                    );
                },
            });
        }

        cols.push(
            {
                key: 'tasks',
                title: 'Linked Tasks',
                flex: 1,
                render: (_, record) => (
                    <TaskDisplay
                        groupAndUsecaseNames={record.group_and_usecase_names}
                        linkedTasks={record.linked_tasks}
                    />
                ),
            },
            {
                key: 'entries',
                title: 'Entries',
                flex: 1,
                render: (_, record) => (
                    <span className="fs-unmask" style={{ fontSize: 14 }}>
                        {record.entries || record.total_records || '0'}
                    </span>
                ),
            },
            {
                key: 'size',
                title: 'Size',
                flex: 1,
                render: (_, record) => (
                    <span className="fs-unmask" style={{ fontSize: 14 }}>
                        {formatFileSize(record?.summary?.num_characters?.sum || 0)}
                    </span>
                ),
            },
            {
                key: 'data_creation_type',
                dataIndex: 'data_creation_type',
                title: 'Data Creation',
                flex: 1,
                render: (val) => (
                    <span className="fs-unmask" style={{ fontSize: 14 }}>
                        {val || ''}
                    </span>
                ),
            },
            {
                key: 'created_by',
                title: 'Created by',
                flex: 1,
                render: (_, record) => (
                    <EllipsisTextWithTooltip
                        text={record.created_by_user?.name || record.created_by_user?.email || record.created_by_user?.sub || '-'}
                        maxWidth={100}
                        style={{ fontSize: 14 }}
                        className="fs-unmask"
                    />
                ),
            },
            {
                key: 'last_modified',
                title: 'Last Modified',
                flex: 1,
                render: (_, record) => (
                    <TooltipTrigger text={formatDateTime(record.last_modified || record.created_at)}>
                        <span className="fs-unmask" style={{ fontSize: 14 }}>
                            {formatDate(record.last_modified || record.created_at)}
                        </span>
                    </TooltipTrigger>
                ),
            },
            {
                key: 'actions',
                width: 56,
                render: (_, record) => {
                    if (role === 'Viewer') return null;
                    const datasetKey = record.key || record.name || record.dataset_name;
                    const isBenchmark = record._groupName === 'Benchmarks';
                    const isManual = record._groupName === 'External Model Results';
                    const isDl = downloadingDatasetKeys.includes(datasetKey);
                    const dlPct = downloadProgressByKey[datasetKey];
                    const isLinked =
                        (record.group_and_usecase_names && Object.keys(record.group_and_usecase_names).length > 0) ||
                        (record.linked_tasks && record.linked_tasks.length > 0);
                    return (
                        <DatasetOverflowMenu
                            dataset={record}
                            isBenchmark={isBenchmark}
                            isManual={isManual}
                            isDownloading={isDl}
                            downloadPct={dlPct}
                            isLinked={isLinked}
                            onDownload={() => handleDownload(record, isBenchmark)}
                            onDelete={() => handleDeleteDataset(record, isBenchmark, isManual)}
                        />
                    );
                },
            }
        );

        return cols;
    }, [
        allExpanded, toggleAllGroups,
        showCheckboxColumn, headerCheckboxStatus, handleHeaderCheckboxChange,
        selectedDatasetKeys, handleCheckboxChange,
        downloadingDatasetKeys, downloadProgressByKey,
        role, handleDeleteDataset,
    ]);

    /* ── Render ────────────────────────────────────────────────────────────── */

    return (
        <>
            {isSelectionMode && (
                <DatasetsBulkActionsBar
                    selectedCount={selectedDatasetKeys.length}
                    totalCount={allDatasets.length}
                    selectedDatasets={selectedDatasets}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onDownload={handleBulkDownload}
                    onDelete={handleBulkDeleteAction}
                    disabled={isBulkOperationInProgress}
                    isDownloading={downloadingDatasetKeys.length > 0}
                    downloadPercent={bulkDownloadPercent}
                />
            )}

            <div className="datasets-grouped-table-wrapper">
            {/* Header-only DataTable — provides column headers */}
            <div className="datasets-header-datatable">
                <DataTable columns={columns} dataSource={[]} showHeader emptyText="" />
            </div>

            {/* Grouped datasets */}
            {groupedDatasets.map((group) => {
                const groupKey = group.group_id || group.group_name;
                const isExpanded = !!expandedGroups[groupKey];

                const enrichedDatasets = group.datasets.map(dataset => ({
                    ...dataset,
                    _groupId: groupKey,
                    _groupName: group.group_name,
                    _key: dataset.key || dataset.name || dataset.dataset_name,
                }));

                return (
                    <React.Fragment key={groupKey}>
                        {/* Expandable group header row */}
                        <div
                            className={`spark-table-row spark-table-row-expandable spark-table-row-interactive${isExpanded ? ' spark-table-row-expandable-open' : ''}`}
                            role="row"
                            onClick={() => toggleGroup(groupKey)}
                            style={{ cursor: 'pointer' }}
                        >
                            {showCheckboxColumn ? (
                                <div
                                    className="spark-table-cell spark-table-cell-checkbox"
                                    role="cell"
                                    aria-hidden="true"
                                />
                            ) : null}
                            <div className="spark-table-cell spark-table-cell-expand">
                                <Button
                                    variant="ghost"
                                    size="small"
                                    className="spark-table-expand-toggle-btn"
                                    aria-expanded={isExpanded}
                                    aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                                    onClick={(e) => { e.stopPropagation(); toggleGroup(groupKey); }}
                                    icon={
                                        <SparkIcon
                                            slug={isExpanded ? 'chevron-down-large' : 'chevron-right-large'}
                                            size="small"
                                        />
                                    }
                                />
                            </div>
                            <div className="spark-table-cell spark-table-cell-expand-parent">
                                <div className="spark-table-cell-two-lines-content">
                                    <div className="spark-table-cell-title-line">
                                        <p className="spark-table-cell-title-text fs-unmask">
                                            {group.group_name}
                                        </p>
                                        <p className="spark-table-cell-title-count fs-unmask">
                                            ({group.datasets.length})
                                        </p>
                                    </div>
                                    {group.subtitle && (
                                        <p className="spark-table-cell-subtitle-text">
                                            {group.subtitle}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="spark-table-cell spark-table-cell-text-button" />
                        </div>

                        {/* Group dataset rows */}
                        {isExpanded && (
                            group.isLoading ? (
                                <div className="datasets-group-loading">
                                    <DataTable
                                        columns={columns}
                                        dataSource={[]}
                                        showHeader={false}
                                        loading
                                    />
                                </div>
                            ) : (
                                group.datasets.length > 0 && (
                                    <DataTable
                                        columns={columns}
                                        dataSource={enrichedDatasets}
                                        showHeader={false}
                                        rowKey={(record) => record._key || record.key || record.name || record.dataset_name}
                                    />
                                )
                            )
                        )}
                    </React.Fragment>
                );
            })}
        </div>
        </>
    );
};

export default DatasetsGroupedTableView;
